<template>
	<div class="d-flex flex-row flex-wrap">
		<template v-if="isDisplayPlaceholders">
			<div
				v-for="i in 4"
				:key="i"
				class="p-0 my-2 d-flex flex-row nowrap justify-content-start align-items-center"
				:class="itemWidthClass"
			>
				<div class="me-2">
					<PlaceholderBox
						:minHeight="`none`"
						:minWidth="`none`"
						:height="`${svgSize}px`"
						:width="`${svgSize}px`"
						:borderRadius="'50%'"
					/>
				</div>
				<PlaceholderBox
					:minHeight="'none'"
					:minWidth="'none'"
					:height="'20px'"
					:width="'100px'"
				/>
			</div>
		</template>

		<template v-else-if="criterias.length > 0">
			<div
				v-for="(criteria, criteria_i) in criterias"
				:key="criteria_i"
				class="p-0 my-2 d-flex flex-row nowrap justify-content-start align-items-center"
				:class="itemWidthClass"
			>
				<div class="me-2 d-flex align-items-center">
					<svg
						:width="svgSize"
						:height="svgSize"
						:alt="criteria.name"
						class="icon text-primary-blue me-2"
					>
						<use :xlink:href="`#___icon___${criteria.svgName}`"></use>
					</svg>
					<span class="text-start">{{ criteria.label }}</span>
				</div>
			</div>
		</template>
	</div>
</template>

<script>
import { mapState, mapGetters } from "vuex";
import PlaceholderBox from "@/vue/planning/components/common/placeholderBox.vue";
import { getDeepCopy } from "@/utils/utils.js";
import store from "@/vue/planning/store/store";

export default {
	store,
	name: "infoIcons",
	props: {
		prestation: {
			type: Object,
			default: () => {
				return {};
			},
		},
		svgSize: {
			type: Number,
			default: 20,
		},
		isDisplayPlaceholders: {
			type: Boolean,
			default: false,
		},
	},
	components: {
		PlaceholderBox,
	},
	computed: {
		...mapState(["content", "screenType"]),
		...mapGetters(["infoIcons_criterias", "locale"]),
		itemWidthClass() {
			if (this.screenType === "mobile") {
				return "col-6";
			} else {
				return "";
			}
		},
		surface() {
			return this.prestation["surface"];
		},
		floorNumber() {
			return this.prestation["floorNumber"];
		},
		floorLabel() {
			return window.content.prestation_floorNumber;
		},
		// animal() {
		//     if (this.prestation.criterias.includes("52")) return true;
		// },
		maxCapacity() {
			if(!this.prestation.variants) return undefined
			const variantWithCapacity = this.prestation.variants.find((variant) => {
				return variant.maxCapacity;
			});
			if (variantWithCapacity) {
				return variantWithCapacity.maxCapacity;
			}
			return undefined;
		},
		criterias() {
			const arr = [];
			const criterias = getDeepCopy(
				this.infoIcons_criterias[this.prestation.prestationId] || []
			);
			
			if (this.maxCapacity) {
				// add capacity
				arr.push({
					id: null,
					svgName: "two-people",
					name: "capacity",
					label: `1-${this.maxCapacity}`,
				});
			}

			// Find bedroom criteria from processed criterias
			const bedroomCriteria = criterias.find(e => e.name === "sleepingRooms");
			
			// Check for room number criteria (339-358) directly from prestation criterias
			const roomNumberCriteriaIds = [
				"358", "357", "356", "355", "354", "353", "352", "351", "350", "349",
				"348", "347", "346", "345", "344", "343", "342", "341", "340", "339"
			];
			const hasRoomNumberCriteria = this.prestation.criterias && 
				this.prestation.criterias.some(criteriaId => roomNumberCriteriaIds.includes(criteriaId));
			
			const foundRoomCriteria = this.prestation.criterias ? 
				this.prestation.criterias.filter(criteriaId => roomNumberCriteriaIds.includes(criteriaId)) : [];

			// Logic for bedroom/room display:
			// - If bedroom = 1 (criteria 319) AND room number is empty → display "Studio"
			// - If bedroom = 1 (criteria 319) AND room number is filled → display "1 chambre"
			// - If bedroom ≠ 1 → display bedroom info normally
			// - If no bedroom criteria AND no room criteria → display nothing
			// Check if bedroom number is "1" (criteria 319)
			if (bedroomCriteria && bedroomCriteria.id === "319") {
				// If bedroom is 1, check room number
				if (hasRoomNumberCriteria) {
					// Room number is filled, display bedroom info
					arr.push(bedroomCriteria);
				} else {
					// Room number is empty, display "studio"
					arr.push({
						id: null,
						svgName: "bed",
						name: "bed",
						label: `Studio`,
					});
				}
			} else if (bedroomCriteria) {
				// Other bedroom numbers (not 1), display bedroom info
				arr.push(bedroomCriteria);
			} else if (hasRoomNumberCriteria) {
				// No bedroom criteria but has room criteria, display 'studio'
				arr.push({
					id: null,
					svgName: "bed",
					name: "bed",
					label: `Studio`,
				});
			} else {
				// No bedroom criteria AND no room criteria, display nothing
			}

			// if (this.animal) {
			//     // add surface
			//     arr.push({
			//         id: null,
			//         svgName: "animals",
			//         name: "animal",
			//         label: `Animaux admis`,
			//     });
			// }

			if (this.surface) {
				// add surface
				arr.push({
					id: null,
					svgName: "square-space",
					name: "surface",
					label: `${this.surface}m2`,
				});
			}

			if (this.floorNumber != null) {
				arr.push({
					id: null,
					// Use the elevator svg until floor number svg is provided
					svgName: "lift",
					name: "floorNumber",
					label: this.floorNumber === "0" ? window.content.prestation_groundFloor : `${this.floorLabel} ${this.floorNumber}`,
				});
			}

			// Only add non-bedroom criteria from the processed list
			criterias.forEach((criteria, index) => {
				if (criteria.name !== "sleepingRooms") {
					arr.push(criteria);
				}
			});

			return arr;
		},
	},
};
</script>

<style scoped></style>
